import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  BarChart3, TrendingUp, Users, Ticket, Clock, AlertCircle,
  Download, RefreshCw, Calendar, Filter, Settings, Eye, X,
  Activity, Zap, Target, Award, TrendingDown, ArrowUpRight,
  ArrowDownRight, PieChart, LineChart, BarChart, DownloadCloud, FileText, FileSpreadsheet
} from 'lucide-react';
import { AdminService } from '../../services/AdminService';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Modal } from '../../components/ui/Modal';
import { Switch } from '../../components/ui/Switch';
import toast from 'react-hot-toast';

const AdvancedAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('7d');
  const [selectedMetric, setSelectedMetric] = useState('all');
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [customMetrics, setCustomMetrics] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState(null);

  // Create AdminService instance (memoized to avoid recreating on each render)
  const adminService = useMemo(() => new AdminService(), []);

  const loadAnalytics = useCallback(async (showToast = false) => {
    try {
      if (showToast) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      const response = await adminService.getAdvancedAnalytics(selectedPeriod);
      if (response.success) {
        setAnalytics(response.data);
        // Initialize custom metrics if available and not already set
        if (response.data?.customMetrics) {
          setCustomMetrics(prev => {
            if (prev.length === 0) {
              return response.data.customMetrics.map(metric => ({
                ...metric,
                enabled: metric.enabled !== false
              }));
            }
            return prev;
          });
        }
        if (showToast) {
          toast.success('Analytics refreshed successfully');
        }
      } else {
        if (showToast) {
          toast.error(response.message || 'Failed to refresh analytics');
        }
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
      if (showToast) {
        toast.error('Failed to refresh analytics');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedPeriod, adminService]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const handleExport = async (format) => {
    try {
      setExporting(true);
      setExportFormat(format);
      
      // Call AdminService export method
      const response = await adminService.exportAnalytics(format, selectedPeriod);
      
      if (!response.success) {
        throw new Error(response.message || 'Export failed');
      }
      
      // In a real app, this would trigger the actual download
      // For now, we'll create a mock download
      const fileName = `analytics-report-${selectedPeriod}-${new Date().toISOString().split('T')[0]}.${format}`;
      
      toast.success(`${format.toUpperCase()} export started. Downloading ${fileName}...`);
      
      // Simulate file download
      const blob = new Blob(['Mock analytics data'], { type: format === 'pdf' ? 'application/pdf' : 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setExporting(false);
      setExportFormat(null);
    } catch (error) {
      console.error('Export error:', error);
      toast.error(`Failed to export as ${format.toUpperCase()}`);
      setExporting(false);
      setExportFormat(null);
    }
  };

  const handleCustomize = () => {
    setShowCustomizeModal(true);
  };

  const handleSaveCustomization = () => {
    // In a real app, this would save the custom metrics preferences
    toast.success('Dashboard customization saved successfully');
    setShowCustomizeModal(false);
  };

  const handleToggleMetric = (index) => {
    setCustomMetrics(prev => {
      const metrics = prev.length > 0 ? prev : (analytics?.customMetrics || []);
      const updated = [...metrics];
      if (updated[index]) {
        updated[index] = {
          ...updated[index],
          enabled: !updated[index].enabled
        };
      }
      return updated;
    });
  };

  const getMetricIcon = (metric) => {
    const icons = {
      tickets: Ticket,
      users: Users,
      response_time: Clock,
      satisfaction: Award,
      resolution: Target,
      escalation: AlertCircle
    };
    return icons[metric] || Activity;
  };

  const getTrendIcon = (trend) => {
    if (trend > 0) return <ArrowUpRight className="w-4 h-4 text-green-500" />;
    if (trend < 0) return <ArrowDownRight className="w-4 h-4 text-red-500" />;
    return <TrendingUp className="w-4 h-4 text-gray-500" />;
  };

  const getTrendColor = (trend) => {
    if (trend > 0) return 'text-green-600';
    if (trend < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Advanced Analytics</h1>
          <p className="text-gray-600 dark:text-gray-400">Comprehensive analytics and insights dashboard</p>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            onClick={handleCustomize}
            className="flex items-center gap-2"
          >
            <Settings className="h-4 w-4" />
            Customize
          </Button>
          <Button
            variant="outline"
            onClick={() => loadAnalytics(true)}
            disabled={loading || refreshing}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${(loading || refreshing) ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between bg-white p-4 rounded-lg border">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Calendar className="w-4 h-4 text-gray-500" />
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1 text-sm"
            >
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
              <option value="1y">Last Year</option>
            </select>
          </div>
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <select
              value={selectedMetric}
              onChange={(e) => setSelectedMetric(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1 text-sm"
            >
              <option value="all">All Metrics</option>
              <option value="tickets">Tickets</option>
              <option value="users">Users</option>
              <option value="response_time">Response Time</option>
              <option value="satisfaction">Satisfaction</option>
              <option value="resolution">Resolution</option>
              <option value="escalation">Escalation</option>
            </select>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            onClick={() => handleExport('pdf')}
            disabled={exporting}
            className="flex items-center gap-2"
          >
            {exporting && exportFormat === 'pdf' ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <FileText className="h-4 w-4" />
            )}
            PDF
          </Button>
          <Button
            variant="outline"
            onClick={() => handleExport('csv')}
            disabled={exporting}
            className="flex items-center gap-2"
          >
            {exporting && exportFormat === 'csv' ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <FileSpreadsheet className="h-4 w-4" />
            )}
            CSV
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {analytics?.keyMetrics?.map((metric, index) => {
          const IconComponent = getMetricIcon(metric.type);
          return (
            <div key={index} className="bg-white p-6 rounded-lg border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{metric.label}</p>
                  <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <IconComponent className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              <div className="flex items-center mt-2">
                {getTrendIcon(metric.trend)}
                <span className={`text-sm font-medium ml-1 ${getTrendColor(metric.trend)}`}>
                  {Math.abs(metric.trend)}% vs last period
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Ticket Trends */}
        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Ticket Trends</h3>
            <div className="flex items-center space-x-2">
              <LineChart className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-600">Line Chart</span>
            </div>
          </div>
          <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
            <div className="text-center">
              <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500">Ticket trends visualization</p>
              <p className="text-sm text-gray-400">Interactive chart showing ticket volume over time</p>
            </div>
          </div>
        </div>

        {/* Response Time Distribution */}
        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Response Time Distribution</h3>
            <div className="flex items-center space-x-2">
              <PieChart className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-600">Pie Chart</span>
            </div>
          </div>
          <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
            <div className="text-center">
              <Clock className="w-12 h-12 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500">Response time distribution</p>
              <p className="text-sm text-gray-400">Visual breakdown of response time categories</p>
            </div>
          </div>
        </div>

        {/* User Activity */}
        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">User Activity</h3>
            <div className="flex items-center space-x-2">
              <BarChart className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-600">Bar Chart</span>
            </div>
          </div>
          <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
            <div className="text-center">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500">User activity patterns</p>
              <p className="text-sm text-gray-400">Daily and weekly user engagement metrics</p>
            </div>
          </div>
        </div>

        {/* Performance Indicators */}
        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Performance Indicators</h3>
            <div className="flex items-center space-x-2">
              <Target className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-600">Gauge Chart</span>
            </div>
          </div>
          <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
            <div className="text-center">
              <Zap className="w-12 h-12 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500">Performance metrics</p>
              <p className="text-sm text-gray-400">SLA compliance and performance indicators</p>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Analytics */}
      <div className="bg-white rounded-lg border">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Detailed Analytics</h3>
          <p className="text-gray-600">Comprehensive breakdown of all metrics</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {analytics?.detailedMetrics?.map((category, index) => (
              <div key={index} className="space-y-4">
                <h4 className="font-medium text-gray-900">{category.name}</h4>
                <div className="space-y-3">
                  {category.metrics.map((metric, metricIndex) => (
                    <div key={metricIndex} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{metric.name}</p>
                        <p className="text-xs text-gray-600">{metric.description}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-gray-900">{metric.value}</p>
                        <div className="flex items-center">
                          {getTrendIcon(metric.trend)}
                          <span className={`text-xs ml-1 ${getTrendColor(metric.trend)}`}>
                            {Math.abs(metric.trend)}%
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Customize Modal */}
      <Modal
        isOpen={showCustomizeModal}
        onClose={() => setShowCustomizeModal(false)}
        title="Customize Dashboard"
        size="lg"
      >
        <div className="p-6">
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Select which metrics to display on your analytics dashboard
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
              {(customMetrics.length > 0 ? customMetrics : analytics?.customMetrics || []).map((metric, index) => {
                const currentMetric = customMetrics.length > 0 ? customMetrics[index] : metric;
                const isEnabled = currentMetric?.enabled !== false;
                return (
                <Card key={index} className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 dark:text-gray-100">{metric.name}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{metric.description}</p>
                    </div>
                    <Switch
                      checked={isEnabled}
                      onCheckedChange={() => handleToggleMetric(index)}
                    />
                  </div>
                  {metric.progress !== undefined && (
                    <div className="mt-3">
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all"
                          style={{ width: `${metric.progress}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{metric.progress}% complete</p>
                    </div>
                  )}
                </Card>
                );
              })}
            </div>

            {(!customMetrics.length && !analytics?.customMetrics?.length) && (
              <div className="text-center py-8">
                <Settings className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500 dark:text-gray-400">No custom metrics available</p>
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowCustomizeModal(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveCustomization}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdvancedAnalytics;
